import { useCallback, useEffect, useState } from "react";
import { SubtitleItem } from "@/shared/types/player";
import videojs from "video.js";
import { usePlayerStore } from "@/features/player/lib/usePlayerStore";
type VideoJsPlayer = ReturnType<typeof videojs>;

interface UseSubtitlesProps {
    subtitles: SubtitleItem[];
    lang: string;
    videoRef: HTMLVideoElement | null;
}

export const useSubtitles = ({
    subtitles,
    lang,
    videoRef,
}: UseSubtitlesProps) => {
    const [activeCue, setActiveCue] = useState<SubtitleItem | null>(null);
    const store = usePlayerStore();

    const addJsonSubtitlesToPlayer = useCallback(
        (player: VideoJsPlayer) => {
            console.log("addJsonSubtitlesToPlayer", lang);
            // Remove old subtitle tracks
            const tracks = player.remoteTextTracks();
            const tracksArray = Array.from(tracks.tracks_);

            tracksArray.forEach((track) => {
                if (track.kind === "subtitles") {
                    player.removeRemoteTextTrack(track);
                }
            });

            if (subtitles.length) {
                const webvttText = generateWebVTT(subtitles);
                const blob = new Blob([webvttText], { type: "text/vtt" });
                const vttUrl = URL.createObjectURL(blob);

                player.addRemoteTextTrack(
                    {
                        kind: "subtitles",
                        src: vttUrl,
                        srclang: lang,
                        label: lang,
                        default: false,
                    },
                    false,
                );
            }
        },
        [subtitles, lang],
    );

    const handleClickSubtitle = useCallback(() => {
        if (!videoRef) return;
        const currentTime = videoRef.currentTime * 1000;
        const currentCue = subtitles.find(
            (cue) => currentTime >= cue.start && currentTime <= cue.end,
        );
        if (currentCue) {
            store.setSelectSub(currentCue);
        }
    }, [subtitles, store, videoRef]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!videoRef) return;
            const currentTime = videoRef.currentTime * 1000;
            const currentCue = subtitles.find(
                (cue) => currentTime >= cue.start && currentTime <= cue.end,
            );
            setActiveCue(currentCue || null);
        }, 100);

        return () => clearInterval(interval);
    }, [subtitles, videoRef]);

    return { activeCue, addJsonSubtitlesToPlayer, handleClickSubtitle };
};

const formatTime = (ms: number) => {
    const date = new Date(0);
    date.setMilliseconds(ms);
    return date.toISOString().substr(11, 12); // HH:MM:SS.mmm
};

const generateWebVTT = (subtitles: SubtitleItem[]) => {
    const header = "WEBVTT\n\n";
    const body = subtitles
        .map((entry, i) => {
            return `${i + 1}
${formatTime(entry.start)} --> ${formatTime(entry.end)}
${entry.caption}\n`;
        })
        .join("\n");
    return header + body;
};
